import { getPrimaryFunder } from './funder';
import { clientRequiresSahMachinery, type ClientFundingCohort } from './client-funding-context';

/**
 * Canonical per-client PROGRAM KIND — the single funding-type discriminator used
 * platform-wide to decide which funding-specific UI / logic applies to a client
 * (profile cards, budget surface, care-plan template, agreement builder, …).
 *
 * This is the SSOT that replaces the ad-hoc binary `isSahFunded()`-vs-everything-
 * else split. It is derived from the client's `funding_programs` array, the
 * client's funding cohort (so a genuine ongoing Home Care Package client is NOT
 * mis-classified as SAH the way the raw `HCP` token would be), and the page-
 * computed residential boundary (residential is funded by the provider day-rate,
 * not a client budget).
 *
 * SAH and HCP-transition both resolve to `SAH` (the transition population is
 * genuinely becoming SAH and runs on the SAH money machinery). A genuine ongoing
 * HCP client — an `HCP` token with NO SAH / transition cohort — resolves to `HCP`.
 */
export type ClientProgramKind =
  | 'SAH'
  | 'HCP'
  | 'CHSP'
  | 'NATSIFACP'
  | 'NDIS'
  | 'DVA'
  | 'TAC'
  | 'PRIVATE'
  | 'RESIDENTIAL'
  | 'GENERIC';

/**
 * Resolve the canonical program kind for a client.
 *
 * Resolution order:
 *   1. `residentialBoundary` (page-computed residential plan / RAC client) → RESIDENTIAL
 *   2. Support at Home machinery applies → SAH (true SAH, HCP transition cohorts,
 *      and legacy un-backfilled rows — see `clientRequiresSahMachinery`)
 *   3. otherwise map the client's primary funder → HCP / CHSP / NATSIFACP / NDIS / DVA / TAC / PRIVATE
 *   4. fallback → GENERIC (recognised-funder-less client)
 *
 * `requiresSahMachinery` may be passed pre-computed (e.g. a page that already
 * derived the SAH gate) to keep a single source of truth for that boolean; when
 * omitted it is derived from `fundingPrograms` + `fundingCohort`. Passing the raw
 * `isSahFunded` boolean here REPRODUCES the legacy behaviour (HCP token → SAH);
 * passing the cohort (or omitting the override) gives the corrected routing where
 * a genuine ongoing HCP client resolves to `HCP`.
 */
export function resolveClientProgramKind(input: {
  fundingPrograms: readonly string[] | null | undefined;
  fundingCohort?: ClientFundingCohort | null;
  residentialBoundary?: boolean;
  requiresSahMachinery?: boolean;
}): ClientProgramKind {
  if (input.residentialBoundary) return 'RESIDENTIAL';

  const usesSah =
    input.requiresSahMachinery ??
    clientRequiresSahMachinery({
      fundingPrograms: input.fundingPrograms,
      fundingCohort: input.fundingCohort ?? null,
    });
  if (usesSah) return 'SAH';

  const primary = getPrimaryFunder(input.fundingPrograms);
  switch (primary) {
    case 'HCP':
      return 'HCP';
    case 'CHSP':
      return 'CHSP';
    case 'NATSIFACP':
      return 'NATSIFACP';
    case 'NDIS':
      return 'NDIS';
    case 'DVA':
      return 'DVA';
    case 'TAC':
      return 'TAC';
    case 'PRIVATE':
      return 'PRIVATE';
    case 'AN_ACC':
    case 'RAC_RESPITE':
      return 'RESIDENTIAL';
    default:
      return 'GENERIC';
  }
}

const PROGRAM_KIND_LABELS: Record<ClientProgramKind, string> = {
  SAH: 'Support at Home',
  HCP: 'Home Care Package',
  CHSP: 'CHSP',
  NATSIFACP: 'NATSIFAC Programme',
  NDIS: 'NDIS',
  DVA: 'DVA',
  TAC: 'TAC',
  PRIVATE: 'Private',
  RESIDENTIAL: 'Residential (AN-ACC)',
  GENERIC: 'Budget',
};

/** Human-readable label for a program kind. */
export function programKindLabel(kind: ClientProgramKind): string {
  return PROGRAM_KIND_LABELS[kind];
}

/**
 * Whether the program kind operates on the Support at Home money machinery
 * (classification, quarter budget, contribution, statements, claims, the "SAH
 * pathway" / annual-budget profile lines). Only `SAH` — which already subsumes
 * the HCP→SAH transition population — qualifies. Use this to gate SAH-specific
 * profile chrome so it never leaks onto HCP/CHSP/NDIS/DVA/TAC/PRIVATE clients.
 */
export function programKindUsesSahMachinery(kind: ClientProgramKind): boolean {
  return kind === 'SAH';
}

/**
 * Whether the program kind has a means-tested client contribution concept that
 * warrants the (non-SAH) Means Testing card. Residential (AN-ACC means-tested
 * fee) and ongoing HCP (income-tested care fee) qualify. PRIVATE / NDIS / CHSP /
 * DVA / TAC have NO means-test concept, so the card must be hidden for them — the
 * SAH contribution surface is handled separately by its own SAH-funded gate.
 */
export function programKindHasMeansTest(kind: ClientProgramKind): boolean {
  return kind === 'RESIDENTIAL' || kind === 'HCP';
}

/**
 * The care-plan `plan_type` that a client of this program kind should get. Mirrors
 * the `CARE_PLAN_TYPE_OPTIONS` values + the `care_plans_plan_type_check` constraint
 * (SUPPORT_AT_HOME / HCP / CHSP / NATSIFACP / NDIS / AN_ACC / DVA / TAC / PRIVATE). SAH and the
 * recognised-but-unmapped GENERIC fallback both map to SUPPORT_AT_HOME (legacy
 * parity — GENERIC is only reached for an unrecognised non-empty funder token, since
 * empty-funder rows resolve to SAH via the fail-open in clientRequiresSahMachinery).
 * RESIDENTIAL maps to AN_ACC.
 */
export function planTypeForProgramKind(kind: ClientProgramKind): string {
  switch (kind) {
    case 'HCP':
      return 'HCP';
    case 'CHSP':
      return 'CHSP';
    case 'NATSIFACP':
      return 'NATSIFACP';
    case 'NDIS':
      return 'NDIS';
    case 'DVA':
      return 'DVA';
    case 'TAC':
      return 'TAC';
    case 'PRIVATE':
      return 'PRIVATE';
    case 'RESIDENTIAL':
      return 'AN_ACC';
    case 'SAH':
    case 'GENERIC':
    default:
      return 'SUPPORT_AT_HOME';
  }
}
